import { deleteApi, getApi } from "../../../../../utils/api.ts";
import {
  API_EMPLOYEE_CERTIFICATION,
  API_EMPLOYEE_CERTIFICATION_MASTER,
  API_MY_EMPLOYEE_CERTIFICATIONS,
} from "../../../../../../config/Endpoints.js";
import type {
  CertificationCategory,
  EmployeeCertificationResponse,
  UpdateEmployeeCertificationsRequest,
} from "../types.ts";

function unwrapApiData<T>(value: T | { data: T }): T {
  if (value != null && typeof value === "object" && "data" in value) {
    return (value as { data: T }).data;
  }
  return value as T;
}

export async function fetchMyEmployeeCertifications(
  accessToken?: string,
  signal?: AbortSignal,
): Promise<EmployeeCertificationResponse> {
  const response = await getApi<EmployeeCertificationResponse>(
    API_MY_EMPLOYEE_CERTIFICATIONS,
    accessToken,
    "json",
    signal,
  );
  return unwrapApiData(response.data);
}

type CertificationMasterResponse = Array<{
  category: string;
  certifications: Array<{
    certificationId: number;
    certificationName: string;
  }>;
}>;

export async function fetchCertificationMaster(
  accessToken?: string,
  signal?: AbortSignal,
): Promise<CertificationCategory[]> {
  const response = await getApi<CertificationMasterResponse>(
    API_EMPLOYEE_CERTIFICATION_MASTER,
    accessToken,
    "json",
    signal,
  );
  return unwrapApiData(response.data).map((category) => ({
    category: category.category,
    certifications: category.certifications.map((certification) => ({
      certificationId: certification.certificationId,
      certification: certification.certificationName,
    })),
  }));
}

export async function updateEmployeeCertifications(
  request: UpdateEmployeeCertificationsRequest,
  accessToken?: string,
): Promise<void> {
  await Promise.all(
    request.certifications.map(async (certification) => {
      const formData = new FormData();
      formData.append("certificationId", String(certification.certificationId));
      formData.append("certificateNumber", certification.certificateNumber);
      formData.append("issueDate", certification.issueDate);
      if (certification.expiryDate) {
        formData.append("expiryDate", certification.expiryDate);
      }
      if (certification.document) {
        formData.append("document", certification.document);
      }

      const response = await fetch(API_EMPLOYEE_CERTIFICATION, {
        method: "PUT",
        headers: {
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: formData,
      });
      if (!response.ok) {
        throw new Error("Unable to update certification.");
      }
    }),
  );
}

export async function deleteEmployeeCertification(
  employeeCertificationId: number,
  accessToken?: string,
): Promise<void> {
  await deleteApi(
    `${API_EMPLOYEE_CERTIFICATION}/${encodeURIComponent(
      String(employeeCertificationId),
    )}`,
    accessToken,
  );
}
